import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button } from "antd";
import { useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Pagination, Navigation } from "swiper";
import "swiper/css/bundle";
import axios from "axios";
import moment from "moment";
import { useNavigate } from "react-router";
import Loader from "../common/Loader";
import CountdownTimer from "../common/CountDown/CountdownTimer";

const settings = {
  slidesPerView: 3,
  spaceBetween: 30,
  pagination: { clickable: true },
  navigation: true,
  breakpoints: {
    620: { slidesPerView: 1, spaceBetween: 20 },
    640: { slidesPerView: 2, spaceBetween: 20 },
    768: { slidesPerView: 2, spaceBetween: 30 },
    1024: { slidesPerView: 3, spaceBetween: 30 },
    1600: { slidesPerView: 4, spaceBetween: 30 },
  },
  modules: [Pagination, Navigation],
};

const DashboardCampaign = (): ReactElement => {
  const { token, type, shopInfo } = useSelector(
    (state: any) => state?.authReducer
  );
  const navigate = useNavigate();
  const [campaignList, setCampaignList] = useState<any[]>([]);
  const [joinedCampaigns, setJoinedCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_READER_API}/admin/campaign/by-type`,
        {
          params: {
            type,
            isActive: true,
            deleted: false,
            withUpcoming: true,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCampaignList(res?.data?.campaigns || []);
    } catch (error) {
      console.error("Fetch campaigns error:", error);
      setCampaignList([]);
    } finally {
      setLoading(false);
    }
  }, [token, type]);

  /**
   * Fetch shop joined campaigns
   */
  const fetchJoinedCampaigns = useCallback(async () => {
    if (!shopInfo?.id) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_CATALOG_READER_API}/campaign-shop/by-shop`,
        {
          params: { shopId: shopInfo?.id },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setJoinedCampaigns(res?.data?.campaigns || []);
    } catch (error) {
      console.error("Fetch joined campaigns error:", error);
      setJoinedCampaigns([]);
    }
  }, [shopInfo?.id, token]);

  useEffect(() => {
    fetchCampaigns();
    fetchJoinedCampaigns();
  }, [fetchCampaigns, fetchJoinedCampaigns]);

  /**
   * Fast lookup using Set (O1)
   */
  const joinedCampaignIds = useMemo(() => {
    return new Set(joinedCampaigns.map((item: any) => item?.id));
  }, [joinedCampaigns]);

  if (loading) {
    return <Loader />;
  }

  if (!campaignList?.length) {
    return null;
  }

  return (
    <div className="bg-white rounded-md">
      <div className="p-5 border-b text-lg font-semibold">Campaign Events</div>

      <div className="px-4">
        <Swiper {...settings} className="mySwiper">
          {campaignList.map((campaign: any) => {
            const joined = joinedCampaignIds.has(campaign?.id);

            return (
              <SwiperSlide key={campaign?.id}>
                <div className="bg-white shadow-md hover:shadow-xl border rounded-md my-6">
                  {/* Countdown */}
                  <div className="flex justify-center py-2 text-gray-600">
                    <CountdownTimer targetDate={campaign?.endAt} />
                  </div>

                  {/* Banner */}
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={campaign?.banner}
                      alt={campaign?.name}
                      className="object-cover w-full h-full"
                    />
                  </div>

                  {/* Details */}
                  <div className="p-4 flex flex-col">
                    <h3 className="text-base text-gray-600 font-semibold mb-4">
                      {campaign?.name}
                    </h3>

                    <Button type="dashed">
                      {campaign?.campaignType?.split("_").join(" ")}
                    </Button>

                    <span className="mb-2 text-gray-600 mt-4">
                      {moment(campaign?.startAt).format("ll")} -{" "}
                      {moment(campaign?.endAt).format("ll")}
                    </span>

                    {!!campaign?.minimumDiscount && (
                      <span>
                        Minimum price discount: {campaign?.minimumDiscount}
                        {campaign?.minimumDiscountType === "PERCENTAGE"
                          ? "%"
                          : "Tk"}
                      </span>
                    )}

                    <span>
                      Registration until:{" "}
                      {moment(campaign?.startAt).format("ll")}
                    </span>
                  </div>

                  {/* Footer */}
                  <div className="p-4 flex items-center justify-between border-t">
                    <span className="text-xs">
                      Sellers: {campaign?.totalSeller || 0}
                    </span>

                    <span className="text-xs">
                      Products: {campaign?.totalProducts || 0}
                    </span>

                    {shopInfo?.id &&
                      (joined ? (
                        <Button
  type="primary"
  style={{ background: "#2E66D0", borderColor: "#2E66D0" }}
  onClick={() =>
    navigate(
      `/shops/${shopInfo?.id}/campaign/${campaign?.id}/details`
    )
  }
>
  View
</Button>
                      ) : (
                        <Button
                          danger
                          type="primary"
                          onClick={() =>
                            navigate(
                              `/shops/${shopInfo?.id}/campaign/${campaign?.id}/assign-products`
                            )
                          }
                        >
                          Join
                        </Button>
                      ))}
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

export default DashboardCampaign;
